/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import {
  Button as ButtonCore,
  type ButtonProps as ButtonCoreProps,
  type ButtonRenderProps,
  type ContextValue,
  DEFAULT_SLOT,
  Provider,
  useContextProps,
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { ImageContext } from '../image/image.component';
import { TextContext } from '../text/text.component';

interface ReplyPreviewProps extends Omit<ButtonCoreProps, 'style' | 'className'> {
  variant?: keyof typeof accentBarVariantVariants;
  style?: StyleOrFunction<ButtonRenderProps>;
}

const ReplyPreviewContext = createContext<ContextValue<ReplyPreviewProps, HTMLButtonElement>>({});

const ReplyPreview = forwardRef((props: ReplyPreviewProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, ReplyPreviewContext);

  const { children, style, variant = 'ghost', ...coreProps } = props;

  return (
    <ButtonCore
      ref={ref}
      {...applyStylesWithRenderProps<ButtonRenderProps>((state) =>
        stylex.props(
          styles.base,
          backgroundVariantVariants[variant],
          typeof style === 'function' ? style(state) : style,
        ),
      )}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        return (
          <Provider
            values={[
              [TextContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    size: 13,
                    weight: 'regular',
                    color: variant === 'ghost' ? 'placeholder' : variant === 'sent' ? 'inverted' : 'primary',
                    style: textStyles.content,
                  },
                  [REPLY_PREVIEW_SLOTS.SENDER]: {
                    size: 14,
                    weight: 'semibold',
                    color: variant === 'ghost' ? 'primary' : variant === 'sent' ? 'inverted' : 'interactive',
                    style: textStyles.authorName,
                  },
                },
              },
              ],
              [ImageContext, { style: imageStyles.base, src: '' }],
            ]}>
            <span aria-hidden="true" {...stylex.props(accentBarStyles.base, accentBarVariantVariants[variant])} />
            {evaluatedChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const styles = stylex.create({
  base: {
    background: 'none',
    borderRadius: 4,
    borderStyle: 'none',
    gridTemplateAreas: '"accent photo author" "accent photo message"',
    outline: 'none',
    alignItems: 'center',
    appearance: 'none',
    boxSizing: 'border-box',
    columnGap: 10,
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr',
    gridTemplateRows: 'auto auto',
    rowGap: 0,
    textAlign: 'start',
    transitionDuration: '0.15s',
    transitionProperty: 'background-color',
    transitionTimingFunction: 'ease-in-out',
    maxWidth: '100%',
    width: '100%',
  },
});

const backgroundVariantVariants = stylex.create({
  received: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-focus-visible])': $.opacity04,
      ':is([data-hovered])': $.opacity04,
      ':is([data-pressed])': $.opacity04,
    },
  },
  sent: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-focus-visible])': $.buttonSurfacePrimary,
      ':is([data-hovered])': $.buttonSurfacePrimary,
      ':is([data-pressed])': $.buttonSurfacePrimary,
    },
  },
  ghost: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-focus-visible])': $.opacity04,
      ':is([data-hovered])': $.opacity04,
      ':is([data-pressed])': $.opacity04,
    },
  }
});

const accentBarStyles = stylex.create({
  base: {
    gridArea: 'accent',
    borderRadius: 2,
    height: 32,
    width: 2,
  }
});

const accentBarVariantVariants = stylex.create({
  received: {
    backgroundColor: $.textInteractive,
  },
  sent: {
    backgroundColor: $.textInverted,
  },
  ghost: {
    backgroundColor: $.iconDisabled
  }
});

const textStyles = stylex.create({
  authorName: {
    gridArea: 'author',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  content: {
    gridArea: 'message',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
});

const imageStyles = stylex.create({
  base: {
    gridArea: 'photo',
    borderRadius: 4,
    objectFit: 'cover',
    height: 32,
    width: 32,
  }
});

const REPLY_PREVIEW_SLOTS = {
  SENDER: 'sender',
} as const;

export { REPLY_PREVIEW_SLOTS, ReplyPreview, ReplyPreviewContext, type ReplyPreviewProps };
